<template>
  <!-- The box, not the player, reserves the layout: until the chunk upgrades it, <mux-player> is an
  unknown element (display: inline), so width/aspect-ratio on the tag itself would do nothing. -->
  <div
    v-if="isReady"
    id="residency-video-box"
    ref="playerBox"
    class="w-full mx-auto bg-black bg-contain bg-center bg-no-repeat"
    :style="boxStyle"
  >
    <mux-player
      id="residency-video-player"
      :playback-id="video?.mux_playback_id"
      :poster="video?.poster ?? undefined"
      stream-type="on-demand"
      preload="none"
      playsinline
      class="block w-full h-full"
      :style="playerStyle"
      accent-color="var(--g-button-guest-primary, var(--button-primary-color, blue))"
    />
  </div>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useIntersectionObserver } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'

import { isVideoAssetReady } from '@/modules/media/utils/media'

import { VideoAssetRead } from '@generated/types'

// Keeps a portrait asset from reserving a ~1600px-tall box in the desktop media column.
const MAX_HEIGHT_VH = 70
// Start fetching before the box is on screen so the player is usable by the time it arrives.
const PRELOAD_MARGIN = '400px'

const props = defineProps<{
  video: VideoAssetRead | null
}>()

const isReady = computed<boolean>(() => isVideoAssetReady(props.video))

const playerBox = ref<HTMLElement | null>(null)
let chunkRequested = false

// Client-only element registration: `@mux/mux-player` calls customElements.define() at import and
// would crash the guest SSR render. The server emits the inert <mux-player> tag and the browser
// upgrades it once this import resolves.
function loadPlayerChunk() {
  if (chunkRequested) return
  chunkRequested = true
  // A failed chunk load leaves the reserved box with just the poster, so surface it rather than fail silently.
  import('@mux/mux-player').catch(err => Sentry.captureException(err))
}

// Fetch the chunk only as the box nears the viewport (Mux's own lazy-loading recipe) so a
// below-the-fold video stays off the guest-page LCP path.
const observer = useIntersectionObserver(
  playerBox,
  ([entry]) => {
    if (!entry?.isIntersecting) return
    loadPlayerChunk()
    observer.stop()
  },
  { rootMargin: PRELOAD_MARGIN },
)

// Without IntersectionObserver the callback above never fires, so upgrade straight away.
onMounted(() => {
  if (!observer.isSupported.value) loadPlayerChunk()
})

// Mux reports the asset ratio as "w:h", and sends "" when it has none.
const aspectParts = computed<[number, number]>(() => {
  const [width, height] = (props.video?.aspect_ratio ?? '').split(':').map(Number)
  return width > 0 && height > 0 ? [width, height] : [16, 9]
})

// Guests get a deliberately minimal player: play/pause, time, volume, fullscreen. Mux exposes one
// custom property per control, and --captions-button / --playback-rate-button also cover their
// menu-button variants through Mux's own fallback chain.
const playerStyle: Record<string, string> = {
  '--top-controls': 'none', // that row only ever holds the video title, so the whole bar goes
  '--seek-backward-button': 'none',
  '--seek-forward-button': 'none',
  '--playback-rate-button': 'none',
  '--captions-button': 'none',
  '--rendition-menu-button': 'none',
  '--audio-track-menu-button': 'none',
  '--airplay-button': 'none',
  '--cast-button': 'none',
  '--pip-button': 'none',
  '--media-preview-time-background': 'white', // Time-tooltip text is always black
}

const boxStyle = computed<Record<string, string>>(() => {
  const [width, height] = aspectParts.value
  const poster = props.video?.poster
  return {
    aspectRatio: `${width} / ${height}`,
    // Width drives height in an aspect-ratio box, so capping width by the ratio caps the height.
    maxWidth: `${Number(((MAX_HEIGHT_VH * width) / height).toFixed(3))}vh`,
    // Paints the poster frame while the chunk is still on the wire; the player has no shadow DOM,
    // and so no poster of its own, until it upgrades.
    ...(poster ? { backgroundImage: `url("${poster}")` } : {}),
  }
})
</script>
